import { isCompanyConfigured } from '@/lib/company-status';
import type { Company, User } from '@/types';

const SETUP_ALLOWED_PATHS = new Set(['/setup', '/settings', '/services', '/professionals', '/working-hours']);

function hasPermissionContext({ user }: { user: User | null | undefined }) {
  return Boolean(user?.permissionCodes?.length) || Boolean(user?.isSuperAdmin);
}

export function getCompanyHomePath(company: Pick<Company, 'status'>): '/dashboard' | '/setup' {
  return isCompanyConfigured(company) ? '/dashboard' : '/setup';
}

export function resolvePrivateLayoutRedirect({
  isAuthenticated,
  user,
  activeCompany,
  canFinishOnboarding,
  pathname,
}: {
  isAuthenticated: boolean;
  user: User | null | undefined;
  activeCompany: Company | null;
  canFinishOnboarding: boolean;
  pathname: string;
}): string | null {
  if (!isAuthenticated) return '/login';
  if (user?.mustChangePassword) return '/change-password';
  if (!activeCompany) return '/select-company';
  if (isCompanyConfigured(activeCompany)) {
    return pathname === '/setup' ? '/dashboard' : null;
  }

  if (SETUP_ALLOWED_PATHS.has(pathname)) {
    return null;
  }

  const hasContext = hasPermissionContext({ user });
  if (hasContext && !canFinishOnboarding) return '/forbidden';

  return '/setup';
}

export function resolveOnboardingRedirect({
  isAuthenticated,
  user,
  activeCompany,
  isCreatingNewCompany,
  canFinishOnboarding,
}: {
  isAuthenticated: boolean;
  user: User | null | undefined;
  activeCompany: Company | null;
  isCreatingNewCompany: boolean;
  canFinishOnboarding: boolean;
}): string | null {
  if (!isAuthenticated) return '/login';
  if (user?.mustChangePassword) return '/change-password';
  if (isCreatingNewCompany) return null;
  if (activeCompany && isCompanyConfigured(activeCompany)) return '/dashboard';
  if (!activeCompany) return '/select-company';

  const hasContext = hasPermissionContext({ user });
  if (hasContext && !canFinishOnboarding) return '/forbidden';

  return '/setup';
}
